import {createContext, use, useMemo} from 'react'
import type {ReactNode} from 'react'

import type {IssuesDashboardLayoutPayload} from '../types/routes-types'
import {useIssuesListResults} from '../hooks/use-issues-list-results'

interface IssuesDashboardSurfaceStateContextValue {
  payload: IssuesDashboardLayoutPayload
  fetchedQuery: string
  currentPage: number
  locationSidebarLinkId?: string
  isLoading: boolean
  fetchResults: (args: {q: string; page: number; sidebarLinkId?: string}) => void
}

const IssuesDashboardSurfaceStateContext = createContext<IssuesDashboardSurfaceStateContextValue | null>(null)

export function IssuesDashboardSurfaceStateProvider({
  children,
  payload,
  isLoading,
}: {
  children: ReactNode
  payload: IssuesDashboardLayoutPayload
  isLoading: boolean
}) {
  const {fetchedQuery, currentPage, fetchResults, locationSidebarLinkId} = useIssuesListResults({
    defaultFilterValue: '',
  })

  const value: IssuesDashboardSurfaceStateContextValue = useMemo(
    () => ({
      payload,
      fetchedQuery,
      currentPage,
      fetchResults,
      locationSidebarLinkId,
      isLoading,
    }),
    [payload, fetchedQuery, currentPage, fetchResults, locationSidebarLinkId, isLoading],
  )

  return <IssuesDashboardSurfaceStateContext value={value}>{children}</IssuesDashboardSurfaceStateContext>
}

export function useIssuesDashboardSurfaceStateContext() {
  const context = use(IssuesDashboardSurfaceStateContext)
  if (!context) {
    throw new Error('useIssuesDashboardSurfaceStateContext must be used within a IssuesDashboardSurfaceStateProvider')
  }
  return context
}
